import { Col, Modal, Row, Select, Table } from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import {
  approveEvent,
  getEventAttendances,
  getEventImage,
  rejectEvent,
} from '../../api/event.api'
import ContentHeader from '../../components/shared/ContentHeader'
import styles from '../../styles/sales.module.scss'

const EventAuthListPage = () => {
  // 서버 페이징 구조에 맞게 초기값 세팅
  const [imageUrls, setImageUrls] = useState({}) // id -> image url 매핑
  const [modalUrl, setModalUrl] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [eventAttendances, setEventAttendances] = useState({
    content: [],
    pageable: { pageNumber: 0, pageSize: 10 },
    totalElements: 0,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [statusFilter, setStatusFilter] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    getEvents(0, eventAttendances.pageable?.pageSize, statusFilter)
    // eslint-disable-next-line
  }, [statusFilter])

  // eventAttendances.content가 변경될 때마다 이미지 URL들을 비동기 요청
  useEffect(() => {
    const fetchImages = async () => {
      const newImageUrls = {}
      await Promise.all(
        eventAttendances.content.map(async (item) => {
          try {
            const res = await getEventImage({
              category: 'USER_EVENT_ATTENDANCE',
              uniqueId: item.id,
            })
            newImageUrls[item.id] = res[0]?.url || null
          } catch (err) {
            newImageUrls[item.id] = null
          }
        }),
      )
      setImageUrls(newImageUrls)
    }

    if (eventAttendances.content.length > 0) {
      fetchImages()
    }
  }, [eventAttendances.content])

  // page, size, status를 받아서 서버에서 데이터 요청
  const getEvents = async (page = 0, size = 10, status = statusFilter) => {
    setIsLoading(true)
    const params = {
      page,
      size,
      status,
      eventId: 2, // eventId가 2인 데이터만 받아오도록 추가
      // null(전체), 'REJECTED', 'APPROVED', 'REQUESTED'
    }
    try {
      const res = await getEventAttendances(params)
      setEventAttendances(res.data)
    } catch (err) {
      console.log('getPrograms err', err)
    } finally {
      setIsLoading(false)
    }
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys)
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === 'Disabled User',
    }),
  }

  // 응답값에 맞는 columns 정의
  const columns = [
    {
      title: '번호',
      key: 'index',
      render: (text, record, index) => {
        // 현재 페이지와 페이지 사이즈를 활용한 전체 인덱스 계산
        const page = eventAttendances.pageable?.pageNumber || 0
        const size = eventAttendances.pageable?.pageSize || 10
        return page * size + index + 1
      },
    },
    {
      title: '이벤트',
      dataIndex: 'eventId',
      key: 'eventId',
      render: (value) => {
        if (value === 1) return '👩🏻‍💻 인스타 CLICK 이벤트'
        if (value === 2) return '🔥인스타 팔로우 이벤트'
        return value
      },
    },
    { title: '회원 아이디', dataIndex: 'userId', key: 'userId' },
    {
      title: '댓글',
      dataIndex: 'comment',
      key: 'comment',
      render: (_, record) => {
        return <div>{record.comment || '-'}</div>
      },
    },
    { title: '요청 일시', dataIndex: 'createdAt', key: 'createdAt' },
    {
      title: '캡쳐 화면',
      dataIndex: 'captureImage',
      key: 'captureImage',
      width: 100,
      render: (_, record) => {
        const url = imageUrls[record.id]
        if (!url) return '-'
        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              setModalUrl(url)
              setIsModalOpen(true)
            }}
            style={{
              color: '#1890ff',
              textDecoration: 'underline',
              fontSize: 12,
            }}
          >
            <img
              src={url}
              alt="캡쳐"
              style={{ maxWidth: 100, maxHeight: 100, objectFit: 'contain' }}
            />
          </a>
        )
      },
    },
    // { title: '업데이트 일시', dataIndex: 'updatedAt', key: 'updatedAt' },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      align: 'center',
      render: (value) => {
        if (value === 'REJECTED') return '반려'
        if (value === 'APPROVED') return '승인'
        if (value === 'REQUESTED') return '요청'
        return value
      },
    },
    {
      title: '처리',
      key: 'confirm',
      width: 100,
      align: 'center',
      render: (_, record) => {
        if (record.eventId !== 2) return null
        if (record.status !== 'REQUESTED') return null
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              style={{
                background: '#52c41a',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '4px 12px',
                cursor: 'pointer',
              }}
              onClick={async (e) => {
                e.stopPropagation()
                const result = await Swal.fire({
                  text: '승인하시겠습니까?',
                  showCancelButton: true,
                  confirmButtonText: '승인',
                  cancelButtonText: '취소',
                  confirmButtonColor: '#3085d6',
                })
                if (result.isConfirmed) {
                  await approveEvent(record.id)
                  await getEvents(
                    0,
                    eventAttendances.pageable?.pageSize,
                    statusFilter,
                  )
                  setSelectedRowKeys([])
                }
              }}
            >
              승인
            </button>
            <button
              style={{
                background: '#ff4d4f',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '4px 12px',
                cursor: 'pointer',
              }}
              onClick={async (e) => {
                e.stopPropagation()
                const result = await Swal.fire({
                  text: '거절하시겠습니까?',
                  showCancelButton: true,
                  confirmButtonText: '거절',
                  cancelButtonText: '취소',
                  confirmButtonColor: '#3085d6',
                })
                if (result.isConfirmed) {
                  await rejectEvent(record.id)
                  await getEvents(
                    0,
                    eventAttendances.pageable?.pageSize,
                    statusFilter,
                  )
                  setSelectedRowKeys([])
                }
              }}
            >
              거절
            </button>
          </div>
        )
      },
    },
  ]

  // 상세 페이지 이동 예시 (필요시 수정)
  const handleRowClick = (record) => {
    // navigate(`/program/detail/${record.program.id}`)
    // 예시: 이벤트 상세 페이지로 이동
    // navigate(`/event/detail/${record.eventId}`)
  }

  return (
    <>
      <ContentHeader title="[혜택] 인스타 팔로우 이벤트 관리" />
      <div
        className={`${styles['product-register-wrap']}`}
        style={{ minWidth: '1024px' }}
      >
        {/* 상태 필터 드롭박스 */}
        <div
          style={{
            margin: '16px 0',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Select
            style={{ width: 160 }}
            value={statusFilter === null ? 'ALL' : statusFilter}
            onChange={(value) => {
              setStatusFilter(value === 'ALL' ? null : value)
            }}
            options={[
              { label: '전체', value: 'ALL' },
              { label: '요청', value: 'REQUESTED' },
              { label: '승인', value: 'APPROVED' },
              { label: '반려', value: 'REJECTED' },
            ]}
          />
        </div>
        {/*상품조회 status*/}
        <Row
          justify="space-around"
          className={`${styles['product-search-product-status-wrap']}`}
        >
          {/*전체*/}
          <Col className={`${styles['product-status-box-col-wrap']}`} span={6}>
            <Row className={`${styles['product-status-box-wrap']}`}>
              <img
                className={`${styles['product-status-icon']}`}
                src={`${process.env.REACT_APP_HOST}/assets/icon/product/total_product.png`}
              />
              <div className={`${styles['product-status-text']}`}>
                <div>전체</div>
                <div>
                  <span className={`${styles['product-status-num']}`}>
                    {eventAttendances.totalElements}
                  </span>{' '}
                  건
                </div>
              </div>
            </Row>
          </Col>
        </Row>
        {/*프로그램 목록*/}
        <Row
          className={`${styles['product-register-border-box']}`}
          wrap={false}
          style={{ marginTop: 20, borderBottom: 0 }}
        >
          <span className={`${styles['product-register-box-title']}`}>
            팔로우 참여 목록 (총{' '}
            <span
              className={`${styles['product-register-box-subtitle']}`}
              style={{ marginLeft: 0 }}
            >
              {eventAttendances.totalElements}
            </span>
            개)
          </span>
        </Row>
        <div
          className={`${styles['product-register-border-box']}`}
          style={{ padding: '20px 25px 40px', height: 'fit-content' }}
        >
          <Table
            columns={columns}
            dataSource={eventAttendances.content}
            loading={isLoading}
            bordered
            size="middle"
            rowKey="id"
            rowClassName="program-table-row"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
            })}
            pagination={{
              current: (eventAttendances.pageable?.pageNumber || 0) + 1,
              pageSize: eventAttendances.pageable?.pageSize || 10,
              total: eventAttendances.totalElements,
              onChange: (page, pageSize) => {
                getEvents(page - 1, pageSize, statusFilter)
              },
              showSizeChanger: false,
            }}
            style={{ height: 'fit-content', cursor: 'pointer' }}
          />
        </div>
      </div>
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={600}
      >
        {modalUrl && (
          <img
            src={modalUrl}
            alt="캡쳐"
            style={{ maxWidth: '100%', maxHeight: 500 }}
          />
        )}
        {/* <div style={{ marginTop: 12, wordBreak: 'break-all' }}>{modalUrl}</div> */}
      </Modal>
    </>
  )
}

export default EventAuthListPage
